// @flow
import { Box, Button, CircularProgress, Container, CssBaseline, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import routes from '../routes';
import Copyright from '../component/Copyright';
import { AuthContext } from '../context/auth';
import { isNotValidEmail } from '../util/validator';
import ApiResponse from '../model/ApiResponse';
import Image from './signup-background.jpg';

const baseUrl = process.env.PUBLIC_URL || '';

const useStyles = makeStyles((theme) => ({
  background: {
    background: `url(${Image}) no-repeat center center fixed`,
    '-webkit-background-size': 'cover',
    '-moz-background-size': 'cover',
    '-o-background-size': 'cover',
    backgroundSize: 'cover',
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#eff0fff1',
    padding: theme.spacing(4),
    borderRadius: '8px',
    boxShadow: theme.shadows[5],
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const SignIn = () => {
  const { t } = useTranslation();
  const { doPost, setToken } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const validate = values => {
    const errors = {};
    if (!values.email) {
      errors.email = t('Required');
    } else if (isNotValidEmail(values.email)) {
      errors.email = t('Invalid email address');
    }
    if (!values.password) {
      errors.password = t('Required');
    } else if (values.password.length < 6) {
      errors.password = t('Invalid password');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const response: ApiResponse = await doPost('/user/login', values);
    if (response.ok && response.data) {
      await setToken(response.data);
    } else {
      const errorMessage = response.message || 'error.unknown';
      enqueueSnackbar(t(errorMessage, errorMessage), {
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.background}>
    <Container component="main" maxWidth="sm">
      <CssBaseline/>
      <div className={classes.paper}>
        <img src={`${baseUrl}/images/logo.png`} alt="" width={40} height={40}/>
        <Typography component="h1" variant="h5">
          {t('Sign in')}
        </Typography>
        <Formik
          initialValues={{
            email: '',
            password: '',
          }}
          validate={validate}
          onSubmit={onSubmit}
        >
          {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label={t('Email')}
                    name="email"
                    autoComplete="email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  <span className={classes.error}>{errors.email && touched.email && errors.email}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    label={t('Password')}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                  />
                  <span className={classes.error}>{errors.password && touched.password && errors.password}</span>
                </Grid>
              </Grid>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                type="submit"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress color="secondary" size="1.2em"/> : null}
              >
                {t('Sign in')}
              </Button>
              <Grid container justify="flex-end">
                <Grid item>
                  {!isSubmitting && <Link to={routes.recoverPassword}>
                    {t('Recover password')}
                  </Link>}
                </Grid>
              </Grid>
              <Grid container justify="flex-end" style={{ marginTop: '10px' }}>
                <Grid item>
                  {!isSubmitting && <Link to={routes.signUp}>
                    {t('Don\'t have an account? Sign Up')}
                  </Link>}
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </div>
      <Box mt={8}>
        <Copyright/>
      </Box>
    </Container>
    </div>
  );
};

export default SignIn;
